const cleanSOPContent = (content: string): string => {
  if (!content) return '';
  
  let cleaned = content;
  
  // Remove source citation fragments that interrupt readability
  cleaned = cleaned.replace(/●\s*\[SOURCE:\s*\][^●\n]*/g, '');
  // Preserve transparency markers while cleaning other citations
  cleaned = cleaned.replace(/\[SOURCE:[^\]]*\](?!\s*-)/g, ''); // Remove generic SOURCE markers but keep transparency ones
  cleaned = cleaned.replace(/\[Based on:[^\]]*\]/g, '');
  
  // Clean up excessive whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  // Ensure proper paragraph breaks
  cleaned = cleaned.replace(/\n\s*\n/g, '\n\n');
  
  return cleaned;
};

// NEW: Function to render text with inline markers and bold text
const renderTextWithInlineMarkers = (text: string) => {
  // Split text by ALL markers while preserving them
  // Updated regex to handle optional spaces after colon and match until ] or end of sentence
  // Added PRESERVED and ADDITION markers for dual-mode processing transparency
  const parts = text.split(/(\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION|PRESERVED|ADDITION|AI ADDITION|ORIGINAL):[^\]]+\]|\[Incorporated from [^\]]+ documentation\]|\[Corrected per [^\]]+ documentation:[^\]]+\]|\[GAP IDENTIFIED: Not found in [^\]]+ documentation[^\]]*\])/);

  return parts.map((part, index) => {
    // Check if this part is a PRESERVED/ORIGINAL marker (content from original document)
    if (part.match(/^\[(?:PRESERVED|ORIGINAL):[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 mx-0.5 whitespace-nowrap align-baseline">
          <span className="text-xs">✓</span>
          <span className="font-medium">Original:</span>
          <span className="font-normal">{markerContent}</span>
        </span>
      );
    }

    // Check if this part is an ADDITION/AI ADDITION marker (AI-added content)
    if (part.match(/^\[(?:ADDITION|AI ADDITION):[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 mx-0.5 whitespace-nowrap align-baseline">
          <span className="text-xs">+</span>
          <span className="font-medium">AI Added:</span>
          <span className="font-normal">{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a Workday/tool documentation marker
    if (part.match(/^\[Incorporated from .+ documentation\]$/) || part.match(/^\[Corrected per .+ documentation:[^\]]*\]$/) || part.match(/^\[GAP IDENTIFIED: Not found in .+ documentation[^\]]*\]$/)) {
      const isIncorporated = part.match(/^\[Incorporated from (.+) documentation\]$/);
      const isCorrected = part.match(/^\[Corrected per (.+) documentation:([^\]]*)\]$/);
      const isGap = part.match(/^\[GAP IDENTIFIED: Not found in (.+) documentation/);
      const platform = (isIncorporated?.[1] || isCorrected?.[1] || isGap?.[1] || 'Workday');
      if (isIncorporated) {
        return (
          <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 mx-0.5 whitespace-nowrap align-baseline">
            <span>📋</span>
            <span>Incorporated from {platform} documentation</span>
          </span>
        );
      }
      if (isCorrected) {
        return (
          <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200 mx-0.5 align-baseline">
            <span>✏️</span>
            <span>Corrected per {platform} documentation:{isCorrected[2]}</span>
          </span>
        );
      }
      if (isGap) {
        return (
          <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200 mx-0.5 align-baseline">
            <span>⚠️</span>
            <span>Not found in {platform} documentation — requires SME verification</span>
          </span>
        );
      }
    }

    // Check if this part is a transparency marker
    if (part.match(/^\[(?:DOCUMENTED|INFERRED|ASSUMED):[^\]]*\]$/)) {
      const markerType = part.match(/^\[(\w+):/)?.[1];
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];

      let bgColor = '';
      let textColor = '';
      let borderColor = '';
      let icon = '';
      let label = '';

      switch (markerType) {
        case 'DOCUMENTED':
          bgColor = 'bg-green-50';
          textColor = 'text-green-700';
          borderColor = 'border-green-200';
          icon = '✓';
          label = 'Original';
          break;
        case 'INFERRED':
          bgColor = 'bg-violet-100';
          textColor = 'text-violet-800';
          borderColor = 'border-violet-200';
          icon = '+';
          label = 'AI Added';
          break;
        case 'ASSUMED':
          bgColor = 'bg-amber-100';
          textColor = 'text-amber-800';
          borderColor = 'border-amber-200';
          icon = '?';
          label = 'AI Assumed';
          break;
      }

      return (
        <span key={index} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor} border ${borderColor} mx-0.5 whitespace-nowrap align-baseline`}>
          <span className="text-xs font-bold">{icon}</span>
          <span className="font-medium">{label}:</span>
          <span className="font-normal">{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a CONTROL POINT marker (inline)
    if (part.match(/^\[CONTROL POINT:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-yellow-50 text-yellow-800 mx-1 border border-yellow-200">
          <span className="text-sm">🔍</span>
          <span className="font-semibold">CONTROL POINT:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a GAP IDENTIFIED marker (inline)
    if (part.match(/^\[GAP IDENTIFIED:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-red-50 text-red-800 mx-1 border border-red-200">
          <span className="text-sm">⚠️</span>
          <span className="font-semibold">GAP:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a SCREENSHOT NEEDED marker (inline)
    if (part.match(/^\[SCREENSHOT NEEDED:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-blue-50 text-blue-800 mx-1 border border-blue-200">
          <span className="text-sm">📸</span>
          <span className="font-semibold">SCREENSHOT:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a FLOWCHART NEEDED marker (inline)
    if (part.match(/^\[FLOWCHART NEEDED:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-green-50 text-green-800 mx-1 border border-green-200">
          <span className="text-sm">📊</span>
          <span className="font-semibold">FLOWCHART:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a CONFLICT marker (inline)
    if (part.match(/^\[CONFLICT:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-orange-50 text-orange-800 mx-1 border border-orange-200">
          <span className="text-sm">⚡</span>
          <span className="font-semibold">CONFLICT:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is an ASSUMPTION marker (inline)
    if (part.match(/^\[ASSUMPTION:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-purple-50 text-purple-800 mx-1 border border-purple-200">
          <span className="text-sm">💡</span>
          <span className="font-semibold">ASSUMPTION:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Check if this part is a SOURCE marker (inline) - show as "Original"
    if (part.match(/^\[SOURCE:[^\]]*\]$/)) {
      const markerContent = part.match(/:\s*([^\]]+)\]/)?.[1];
      return (
        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium bg-green-50 text-green-700 mx-1 border border-green-200">
          <span className="text-sm font-bold">✓</span>
          <span className="font-semibold">Original:</span>
          <span>{markerContent}</span>
        </span>
      );
    }

    // Handle bold text (**text**) and remove orphan **
    if (part.includes('**')) {
      // Remove orphan ** (not paired properly)
      let cleanedPart = part.replace(/\*\*/g, '');

      // Now try to find proper bold pairs
      const boldMatches = part.match(/\*\*(.+?)\*\*/g);
      if (boldMatches && boldMatches.length > 0) {
        // Has proper bold pairs, split and render
        const boldParts = part.split(/(\*\*[^*]+?\*\*)/);
        return boldParts.map((boldPart, boldIndex) => {
          if (boldPart.match(/^\*\*.+?\*\*$/)) {
            const boldText = boldPart.replace(/\*\*/g, '').trim();
            if (boldText) {
              return <strong key={`${index}-${boldIndex}`} className="font-semibold">{boldText}</strong>;
            }
          }
          return boldPart;
        });
      }
      // No proper pairs, just return cleaned part without **
      return cleanedPart;
    }

    // Regular text - trim leading/trailing spaces to prevent spacing issues
    return part.trim();
  });
};

type ViewMode = 'all' | 'original' | 'additions';

// Filter content based on view mode
const filterContentByViewMode = (content: string, viewMode: ViewMode): string => {
  if (viewMode === 'all') return content;

  // Original content markers (preserve these in 'original' mode)
  const originalMarkersRegex = /\[(DOCUMENTED|SOURCE|PRESERVED|ORIGINAL):[^\]]+\]/g;
  // AI-added content markers (preserve these in 'additions' mode)
  const additionMarkersRegex = /\[(INFERRED|ASSUMED|ADDITION|AI ADDITION):[^\]]+\]/g;

  if (viewMode === 'original') {
    // Keep lines that contain original markers, remove addition markers from display
    const lines = content.split(/\n+/);
    return lines
      .map(line => {
        // Remove addition markers from the line
        return line.replace(additionMarkersRegex, '').trim();
      })
      .filter(line => {
        const hasOriginal = originalMarkersRegex.test(line);
        originalMarkersRegex.lastIndex = 0;
        // Keep if has original markers OR has no markers at all (plain text with content)
        return hasOriginal || line.trim().length > 0;
      })
      .join('\n\n');
  }

  if (viewMode === 'additions') {
    // Extract ONLY the AI-added parts from each line
    const lines = content.split(/\n+/);
    const additionLines: string[] = [];

    lines.forEach(line => {
      // Check if line has addition markers
      const hasAddition = additionMarkersRegex.test(line);
      additionMarkersRegex.lastIndex = 0;

      if (hasAddition) {
        // Extract only the addition markers and their content
        const matches = line.match(additionMarkersRegex);
        if (matches) {
          additionLines.push(matches.join(' '));
        }
      }
    });

    return additionLines.join('\n\n');
  }

  return content;
};

const SOPContentRenderer: React.FC<{ content: string; viewMode?: ViewMode }> = ({ content, viewMode = 'all' }) => {
    if (!content) return null;

    // Apply view mode filtering
    const filteredContent = filterContentByViewMode(content, viewMode);

    // Split content into lines based on various delimiters
    const splitIntoLines = (text: string) => {
      // CRITICAL FIX: Convert escaped newlines (\n) to actual newlines first!
      let processedText = text
        // Convert literal \n\n to actual double newlines (for definitions spacing)
        .replace(/\\n\\n/g, '\n\n')
        // Convert single \n to actual newline BUT keep markers intact
        .replace(/\\n(?!\s*[A-Z][^:]+:)/g, '\n')
        // Join broken markers back together (e.g., "[DOCUMENTED:\ntext]" → "[DOCUMENTED: text]")
        .replace(/(\[(?:DOCUMENTED|INFERRED|ASSUMED|GAP IDENTIFIED|CONTROL POINT|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):)\s*\n\s*/g, '$1 ')
        // CRITICAL: Full stop followed by space and capital letter OR bracket = NEW PARAGRAPH
        .replace(/\.\s+([A-Z\[])/g, '.\n\n$1')
        // DON'T add line breaks before numbers - database already has proper newlines!
        // Add line breaks before role titles that might be running together
        .replace(/(New Analyst:|Risk Analyst:|Project Manager:|Estimator:|Data Validator:|Statistical Analyst:|Excel User Requirements:|Excel Configuration:|Template Access:|Enable Editing:)/g, '\n$1')
        // Add line breaks before system requirement sections
        .replace(/(?<!^|\n)(Must have|Set Calculation|Statistical PERT|Disable Protected)/g, '\n$1');

      // Then split by actual newlines
      let parts = processedText.split('\n');

      // DON'T split by markers - keep them inline with text!
      return parts.filter(p => p.trim().length > 0);
    };
    
    const lines = splitIntoLines(filteredContent);

    // Show empty state if no content matches the filter
    if (lines.length === 0 && viewMode !== 'all') {
      return (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">
            {viewMode === 'original'
              ? 'No original content markers found in this section.'
              : 'No AI-added content markers found in this section.'}
          </p>
        </div>
      );
    }

    return (
      <div className="prose prose-gray max-w-none space-y-1">
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          
          if (!trimmedLine) return null;
          
          // Section headers - FIXED: Support multi-level numbering (1.0, 5.1, 5.1.2, 5.1.2.3, etc.)
          const sectionMatch = trimmedLine.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
          if (sectionMatch) {
            // DEBUG LOG
            if (index < 5) {
              console.log(`📌 Line ${index}: "${trimmedLine.substring(0, 50)}"`);
              console.log(`   Matched number: "${sectionMatch[1]}" | Title: "${sectionMatch[2].substring(0, 30)}"`);
            }

            // Clean up the title - remove trailing colons and clean up spacing
            const cleanTitle = sectionMatch[2].replace(/:\s*$/, '').trim();

            return (
              <div key={index} className="mt-6 mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-md  p-2">
                    {sectionMatch[1]}
                  </span>
                  <h2 className="text-md text-gray-900 m-0">
                    {cleanTitle}
                  </h2>
                </div>
              </div>
            );
          }

          // IMPROVED: Handle role definitions - FIXED: Support multi-level numbering and colon formatting
          const roleMatch = trimmedLine.match(/^(\d+(?:\.\d+)+)\s+(.+)$/);
          if (roleMatch && !sectionMatch) {
            const fullNumber = roleMatch[1]; 
            const restOfLine = roleMatch[2];

            
            const numberParts = fullNumber.split('.');
            const badgeNumber = numberParts.length >= 2
              ? numberParts.slice(-2).join('.')
              : fullNumber;

            return (
              <div key={index} className="mt-4 mb-2">
                <div className="flex items-start gap-3">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">
                    {badgeNumber}
                  </span>
                  <div className="flex-1">
                    <span className="text-gray-700 leading-relaxed">
                      <strong className="font-semibold">{fullNumber}</strong> {restOfLine}
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          
          // Bold text - should be on its own line
          if (trimmedLine.startsWith('**') && trimmedLine.includes('**')) {
            const boldText = trimmedLine.replace(/\*\*/g, '');
            return (
              <div key={index} className="text-gray-900 mt-3 mb-1">
                {renderTextWithInlineMarkers(boldText)}
              </div>
            );
          }
          
          // Bullet points
          if (trimmedLine.startsWith('- ')) {
            const bulletText = trimmedLine.substring(2);
            return (
              <div key={index} className="flex items-start gap-2 ml-4 mb-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700">{renderTextWithInlineMarkers(bulletText)}</span>
              </div>
            );
          }
          
          // Block-level Control Point (standalone)
          if (trimmedLine.match(/^\[CONTROL POINT:/)) {
            const match = trimmedLine.match(/\[CONTROL POINT:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-yellow-50 border-l-4 border-yellow-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔍</span>
                    <span className="text-yellow-800 font-medium">Control Point: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Gap Identified (standalone)
          if (trimmedLine.match(/^\[GAP IDENTIFIED:/)) {
            const match = trimmedLine.match(/\[GAP IDENTIFIED:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-red-50 border-l-4 border-red-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⚠️</span>
                    <span className="text-red-800 font-medium">Gap: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Screenshot Needed (standalone)
          if (trimmedLine.match(/^\[SCREENSHOT NEEDED:/)) {
            const match = trimmedLine.match(/\[SCREENSHOT NEEDED:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-blue-50 border-l-4 border-blue-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📸</span>
                    <span className="text-blue-800 font-medium">Screenshot: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Flowchart Needed (standalone)
          if (trimmedLine.match(/^\[FLOWCHART NEEDED:/)) {
            const match = trimmedLine.match(/\[FLOWCHART NEEDED:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-green-50 border-l-4 border-green-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📊</span>
                    <span className="text-green-800 font-medium">Flowchart: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Assumption (standalone)
          if (trimmedLine.match(/^\[ASSUMPTION:/)) {
            const match = trimmedLine.match(/\[ASSUMPTION:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-purple-50 border-l-4 border-purple-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💡</span>
                    <span className="text-purple-800 font-medium">Assumption: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Source (standalone) - show as "Original"
          if (trimmedLine.match(/^\[SOURCE:/)) {
            const match = trimmedLine.match(/\[SOURCE:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-green-50 border-l-4 border-green-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-green-600">✓</span>
                    <span className="text-green-800 font-medium">Original: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Block-level Conflict (standalone)
          if (trimmedLine.match(/^\[CONFLICT:/)) {
            const match = trimmedLine.match(/\[CONFLICT:\s*([^\]]+)\]/);
            if (match) {
              return (
                <div key={index} className="bg-orange-50 border-l-4 border-orange-400 p-3 my-2 rounded-r-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⚡</span>
                    <span className="text-orange-800 font-medium">Conflict: {match[1]}</span>
                  </div>
                </div>
              );
            }
          }
          
    
          const isNumberedLine = /^\d+\.\d+/.test(trimmedLine);

          return (
            <div key={index} className="text-gray-700  leading-relaxed mb-1">
              {renderTextWithInlineMarkers(trimmedLine)}
            </div>
          );
        })}
      </div>
    );
};

export default SOPContentRenderer;